// ============================================================================
// DÉCONNEXION D'UNE SURFACE WEB FÉDÉRÉE — la séquence, en un seul endroit.
//
// ⚠️ POURQUOI PARTAGÉ : les trois consoles (Console, store privé, EDI) sont des RP du même IdP et
// doivent se déconnecter de la même façon. Chacune l'avait réimplémentée, et toutes les trois avaient
// le MÊME défaut — révoquer la session locale et s'arrêter là. Or cela ne déconnecte pas : tant que la
// session de l'IdP vit, la navigation suivante ré-authentifie EN SILENCE et l'utilisateur se retrouve
// connecté sans avoir rien saisi, persuadé d'être sorti (constaté en production le 2026-08-11).
//
// DEUX TEMPS, ET L'ORDRE COMPTE :
//   1. révocation LOCALE par un POST protégé CSRF (fourni par l'app, qui seule sait parler à son API) ;
//   2. navigation vers `/v1/auth/oidc/logout`, qui termine la session de l'IdP.
// ============================================================================

/** Ce que l'application fournit : l'appel qui révoque SA session (POST protégé CSRF). */
export type RevokeLocalSession = () => Promise<unknown>;

export interface SignOutOptions {
  /** Route de fin de session du RP. Ne changez ceci que si l'app monte ses routes ailleurs. */
  idpLogoutPath?: string;
  /** Où signaler un échec — l'app passe son `alert`. Absent : l'erreur est relancée à l'appelant. */
  onError?: (message: string) => void;
}

/**
 * Déconnecte pour de bon : session locale, puis session de l'IdP.
 *
 * ⚠️ NE PAS passer par le `logout()` d'un contexte React. Ceux-ci mettent l'utilisateur à `null`, ce
 * qui réveille la garde de route AVANT que la navigation ne parte : elle relance une connexion SSO,
 * qui réussit en silence, et la déconnexion est perdue. C'est une course, donc un défaut intermittent
 * — le pire à diagnostiquer. On appelle donc l'API directement, sans toucher à l'état React.
 *
 * ⚠️ ÉCHEC = ON NE NAVIGUE PAS. Afficher une déconnexion qui n'a pas eu lieu est le plus grave des
 * mensonges d'écran : l'utilisateur s'éloigne d'un poste qu'il croit fermé (§23.0).
 */
export async function signOut(revokeLocalSession: RevokeLocalSession, opts: SignOutOptions = {}): Promise<void> {
  const { idpLogoutPath = '/v1/auth/oidc/logout', onError } = opts;
  try {
    await revokeLocalSession();
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    if (!onError) throw e;
    onError(message);
    return;
  }
  window.location.href = idpLogoutPath;
}
